import { isNonEmpty } from '@sgme/fp';

import type { BlotterEntryStatus } from '@/store/blotter/api/types/BlotterEntryStatus.ts';
import {
  type BlotterLegOptionProductType,
  type BlotterSingleOrCompoundOptionProductType,
  type BlotterTradeEntry,
  type BlotterTradeOptionType,
  isOptionLegProductType,
  isOptionProductType,
} from '@/store/blotter/api/types/BlotterTradeEntry.ts';
import type { BlotterEntry } from '@/store/blotter/types/BlotterEntry.ts';
import type { Currency, CurrencyPair } from '@/types/currency.ts';
import type { ISODate, ISODateTime } from '@/types/date.ts';
import { parseDecimal } from '@/utils/number.ts';
import type { Rate } from '@/utils/rates.ts';

export type BlotterOptionEntryCommon = BlotterEntry & {
  //
  // technical fields
  //
  status: BlotterEntryStatus;
  execTime: ISODateTime; // "exec time" or "trade date" in legacy fx, depending on the context ...
  updateTime: ISODateTime;

  // who
  account: string;
  portfolio: string;
  sales: string;
  venue: string;
  electronicAccount: string | null;
  clientId: number | null;

  // what
  optionType: BlotterTradeOptionType | null;
  currencyPair: CurrencyPair;
  expiry: ISODate | null;
  dealtAmount: number | null;
  dealtCurrency: Currency;
  contraAmount: number | null;
  contraCurrency: Currency;
  premium: number | null;
  premiumCurrency: Currency | '' | null;
  premiumSide: 'Pay' | 'Receive' | '' | null;
  premiumPaymentDate: ISODate | null;
  spot: Rate | null;
  strike: number | null;
  forwardRate: number | null;
  way: 'Sell' | 'Buy' | 'Both' | '';
  deliveryDate: ISODateTime | null;
  deliveryType: string | 'Physical_Spot' | null;
  cutOffPlace: string | null;
  callabilityStartDate: ISODate | null;
};

export type BlotterEntrySingleOrCompound = BlotterOptionEntryCommon & {
  product: BlotterSingleOrCompoundOptionProductType;
  strategyReference: null;
};

export type BlotterOptionLegEntry = BlotterOptionEntryCommon & {
  strategyReference: string; // the xonereference/id of the 'compound' option trade this leg is refering to
  product: BlotterLegOptionProductType;
};

export type BlotterOptionEntry = BlotterEntrySingleOrCompound | BlotterOptionLegEntry;

/**
 * Converts a Trade entry coming from webapi to a blotter option entry exploitable by the front.
 * The input is expected to be of blotter type 'Option', will blow otherwise.
 * @param trade A trade entry coming from webapi
 * @returns a blotter option entry exploitable by the front
 */
export const convertOptionEntry = (trade: BlotterTradeEntry): BlotterOptionEntry => {
  if (trade.blotterType !== 'Option') {
    throw Error('dev error');
  }

  const commonProperties: BlotterOptionEntryCommon = {
    clientId: trade.counterpartyBdrId,
    account: trade.accountLongName ?? trade.account,
    execTime: trade.date,
    sales: trade.salesFullName,

    // unchanged
    id: trade.id,
    updateTime: trade.updateTime,
    status: trade.status,
    currencyPair: trade.currencyPair,
    expiry: trade.expiry,
    dealtAmount: trade.dealtAmount,
    dealtCurrency: trade.dealtCurrency,
    contraAmount: parseDecimal(trade.contraAmount),
    contraCurrency: trade.contraCurrency,
    portfolio: trade.portfolio,
    premium: parseDecimal(trade.premium),
    premiumCurrency: trade.premiumCurrency,
    premiumSide: trade.premiumSide,
    premiumPaymentDate: trade.premiumPaymentDate,
    optionType: trade.optionType,
    spot: parseDecimal(trade.spot),
    strike: parseDecimal(trade.strike),
    forwardRate: parseDecimal(trade.forwardRate),
    venue: trade.venue,
    way: trade.way,
    electronicAccount: trade.electronicAccount,
    deliveryDate: trade.deliveryDate,
    deliveryType: trade.deliveryType,
    cutOffPlace: trade.cutOffPlace,
    callabilityStartDate: trade.callabilityStartDate,
  };

  if (isNonEmpty(trade.strategyReference)) {
    // we have a leg
    if (!isOptionLegProductType(trade.product)) {
      // this is unexpected. Worth a warning, but we must at least try to display it
      console.warn(
        `convertOptionEntry: unexpected product type "${trade.product}" for trade id=${trade.id} (which is supposed to be a leg of ${trade.strategyReference}), will try to display it anyway`,
      );
    }

    return {
      ...commonProperties,
      product: trade.product,
      strategyReference: trade.strategyReference,
    } as BlotterOptionLegEntry;
  }

  // this is a single option trade or a compound trade
  if (!isOptionProductType(trade.product)) {
    // this mean the blotterType and the product field of the trade does not agree
    // on prod env it has been observed that a trade with blotterType === 'Option' has product type 'Spot' (unexpected)
    //   what legacy fx does ? it displays it, so do we
    console.warn(
      `convertOptionEntry: unexpected product type "${trade.product}" for trade id=${trade.id}, will try to display it as option in blotter anyway`,
    );
  }

  return { ...commonProperties, product: trade.product, strategyReference: null } as BlotterEntrySingleOrCompound;
};
